/**
 * Sign-in page.
 *
 * The API is deliberately vague about why a login failed (it must not confirm
 * which emails exist), so this page shows the server's message verbatim rather
 * than inventing a more specific one.
 */

import { useState } from 'react';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.jsx';
import { IconAlert } from '../components/Icons.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    setSubmitting(true);

    try {
      await login(email.trim(), password);
      // No navigate() call: the router swaps to the app shell as soon as the
      // auth context reports a user, so routing here would race it.
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(
          caught.status === 429
            ? 'Too many attempts. Wait five minutes and try again.'
            : caught.message,
        );
      } else {
        setError('Something went wrong. Check the API log for details.');
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-brand">
          <span className="sidebar-brand-mark">LS</span>
          <span className="spacer" />
          <ThemeToggle />
        </div>

        <h1 className="login-title">Leads Scanner</h1>
        <p className="login-subtitle">
          Turn a UK SIC code into an enriched list of companies, directors and contacts,
          sourced from Companies House.
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="field">
            <label className="field-label" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              className={`input${error ? ' has-error' : ''}`}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoFocus
              required
              disabled={submitting}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="login-password">
              Password
            </label>
            <input
              id="login-password"
              className={`input${error ? ' has-error' : ''}`}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="alert alert-error" role="alert">
              <span className="alert-icon">
                <IconAlert />
              </span>
              <span className="alert-body">{error}</span>
            </div>
          )}

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={submitting}>
            {submitting && <span className="spinner" />}
            {submitting ? 'Signing in' : 'Sign in'}
          </button>
        </form>

        <div className="login-footer">
          Single-user tool. Credentials are set by <code>ADMIN_EMAIL</code> and{' '}
          <code>ADMIN_PASSWORD</code> in <code>apps/api/.env</code>; change them and re-run{' '}
          <code>pnpm run db:seed</code> to reset.
        </div>
      </div>
    </div>
  );
}
